import {
  derivePersonas,
  deriveRecipientStatuses,
  keepKnownValues,
} from './audienceDerivation'

export type ThemeAttributeSource = 'brand' | 'theme' | null

export interface BrandThemeFields {
  brand_ru: string
  theme: string
}

export interface AudienceFields {
  gender: string
  age: string
  personas: string[]
  recipient_statuses: string[]
}

export interface AudienceDictionaries {
  persona?: string[]
  recipient_status?: string[]
}

export function setBrandAttribute<T extends BrandThemeFields>(
  fields: T,
  value: string,
): { fields: T; source: ThemeAttributeSource } {
  const trimmed = value.trim()
  return {
    fields: { ...fields, brand_ru: value, theme: trimmed ? trimmed : '' },
    source: trimmed ? 'brand' : null,
  }
}

export function setThemeAttribute<T extends BrandThemeFields>(
  fields: T,
  value: string,
): { fields: T; source: ThemeAttributeSource } {
  return {
    fields: { ...fields, theme: value },
    source: value.trim() ? 'theme' : null,
  }
}

export function themeAttributeLocks(source: ThemeAttributeSource): {
  brandLocked: boolean
  themeLocked: boolean
} {
  return {
    brandLocked: source === 'theme',
    themeLocked: source === 'brand',
  }
}

export function resolveThemeAttributeSource(
  brand: string | null | undefined,
  theme: string | null | undefined,
): ThemeAttributeSource {
  const normalizedBrand = brand?.trim() ?? ''
  const normalizedTheme = theme?.trim() ?? ''
  if (normalizedBrand && (!normalizedTheme || normalizedBrand === normalizedTheme)) return 'brand'
  if (normalizedTheme && !normalizedBrand) return 'theme'
  return null
}

export function deriveAudienceAttributes<T extends AudienceFields>(
  fields: T,
  field: 'gender' | 'age',
  value: string,
  dictionaries: AudienceDictionaries,
): T {
  const next = { ...fields, [field]: value }
  return {
    ...next,
    personas: keepKnownValues(
      derivePersonas(next.gender, next.age),
      dictionaries.persona,
    ),
    recipient_statuses: keepKnownValues(
      deriveRecipientStatuses(next.gender, next.age),
      dictionaries.recipient_status,
    ),
  }
}
